import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import {
  Button,
  Card,
  Badge,
  DataTable,
  Input,
  Select,
  Textarea,
  Modal,
} from "../components/ui";
import { ConfirmModal } from "../components/ui/Modal";

const TIPOLOGIE_EDIFICIO = {
  100000000: "Edificio storico",
  100000001: "Edificio moderno",
  100000002: "Villa",
  100000003: "Complesso residenziale",
  100000004: "Altro",
};

const TIPO_UTENZA_OPTIONS = {
  100000000: { label: "Energia elettrica", icon: "\u26A1" },
  100000001: { label: "Gas naturale", icon: "\uD83D\uDD25" },
  100000002: { label: "Acqua", icon: "\uD83D\uDCA7" },
  100000003: { label: "Teleriscaldamento", icon: "\uD83C\uDF21\uFE0F" },
  100000004: { label: "Telecomunicazioni", icon: "\uD83D\uDCE1" },
  100000005: { label: "Rifiuti", icon: "\u267B\uFE0F" },
  100000007: { label: "Rifiuti non speciali", icon: "\u267B\uFE0F" },
  100000008: { label: "Rifiuti speciali", icon: "\u2623\uFE0F" },
  100000006: { label: "Altro", icon: "\uD83D\uDCE6" },
};

const EMPTY_CONTATORE_FORM = {
  codice: "",
  tipoutenza: "",
  codicecliente: "",
  indirizzo: "",
  potenzaimpegnata: "",
  attivo: true,
  dataattivazione: "",
  datadisattivazione: "",
  note: "",
  fornitoreId: "",
};

function DettaglioImmobilePage() {
  const { id } = useParams();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [immobile, setImmobile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Impianti Fotovoltaici state
  const [impiantiFV, setImpiantiFV] = useState([]);
  const [loadingImpiantiFV, setLoadingImpiantiFV] = useState(true);

  // Contatori state
  const [contatori, setContatori] = useState([]);
  const [loadingContatori, setLoadingContatori] = useState(true);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [contatoreModalOpen, setContatoreModalOpen] = useState(false);
  const [editingContatore, setEditingContatore] = useState(null);
  const [contatoreForm, setContatoreForm] = useState(EMPTY_CONTATORE_FORM);
  const [savingContatore, setSavingContatore] = useState(false);
  const [deleteContatoreModalOpen, setDeleteContatoreModalOpen] =
    useState(false);
  const [contatoreToDelete, setContatoreToDelete] = useState(null);

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const isDirezione = user?.ruolo === 100000002;
  const canEdit = isAdmin || isRspp || isDirezione;
  const canDelete = isAdmin;

  const loadContatori = useCallback(async () => {
    setLoadingContatori(true);
    try {
      const data = await apiRequest(`/immobili/${id}/contatori`);
      setContatori(data || []);
    } catch {
      setContatori([]);
    } finally {
      setLoadingContatori(false);
    }
  }, [id, apiRequest]);

  const loadImpiantiFV = useCallback(async () => {
    setLoadingImpiantiFV(true);
    try {
      const data = await apiRequest(
        `/impianti?immobileId=${id}&tipoImpianto=100000000`,
      );
      setImpiantiFV(data.value || []);
    } catch {
      setImpiantiFV([]);
    } finally {
      setLoadingImpiantiFV(false);
    }
  }, [id, apiRequest]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await apiRequest(`/immobili/${id}`);
        setImmobile(data);
      } catch (error) {
        console.error("Error fetching immobile:", error);
        showToast("Errore nel caricamento dell'immobile", "error");
        navigate("/rsa");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    loadContatori();
    loadImpiantiFV();
    apiRequest("/fornitori")
      .then((res) => setFornitoriList(res.value || []))
      .catch(() => {});
  }, [id, apiRequest, navigate, showToast, loadContatori, loadImpiantiFV]);

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiRequest(`/immobili/${id}`, { method: "DELETE" });
      showToast("Immobile eliminato con successo", "success");
      if (immobile?.rsaId) {
        navigate(`/rsa/${immobile.rsaId}`);
      } else {
        navigate("/rsa");
      }
    } catch (error) {
      console.error("Error deleting immobile:", error);
      showToast(error.message || "Errore durante l'eliminazione", "error");
    } finally {
      setDeleteLoading(false);
      setDeleteModalOpen(false);
    }
  };

  // --- Contatori CRUD handlers ---
  const openCreateContatore = () => {
    setEditingContatore(null);
    setContatoreForm(EMPTY_CONTATORE_FORM);
    setContatoreModalOpen(true);
  };

  const openEditContatore = (item) => {
    setEditingContatore(item);
    setContatoreForm({
      codice: item.codice || "",
      tipoutenza:
        item.tipoutenza !== null && item.tipoutenza !== undefined
          ? String(item.tipoutenza)
          : "",
      codicecliente: item.codicecliente || "",
      indirizzo: item.indirizzo || "",
      potenzaimpegnata: item.potenzaimpegnata ?? "",
      attivo: item.attivo !== false,
      dataattivazione: item.dataattivazione?.split("T")[0] || "",
      datadisattivazione: item.datadisattivazione?.split("T")[0] || "",
      note: item.note || "",
      fornitoreId: item.fornitoreId || "",
    });
    setContatoreModalOpen(true);
  };

  const handleContatoreFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContatoreForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveContatore = async () => {
    if (!contatoreForm.codice) {
      showToast("Il codice POD/PDR è obbligatorio", "warning");
      return;
    }

    setSavingContatore(true);
    try {
      const payload = {
        codice: contatoreForm.codice,
        tipoutenza: contatoreForm.tipoutenza
          ? parseInt(contatoreForm.tipoutenza)
          : null,
        codicecliente: contatoreForm.codicecliente || null,
        indirizzo: contatoreForm.indirizzo || null,
        potenzaimpegnata: contatoreForm.potenzaimpegnata
          ? parseFloat(contatoreForm.potenzaimpegnata)
          : null,
        attivo: contatoreForm.attivo,
        dataattivazione: contatoreForm.dataattivazione || null,
        datadisattivazione: contatoreForm.datadisattivazione || null,
        note: contatoreForm.note || null,
        rsaId: immobile.rsaId,
        immobileId: id,
        fornitoreId: contatoreForm.fornitoreId || null,
      };

      if (editingContatore) {
        await apiRequest(`/contatori/${editingContatore.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        showToast("Contatore aggiornato", "success");
      } else {
        await apiRequest("/contatori", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        showToast("Contatore creato", "success");
      }

      setContatoreModalOpen(false);
      loadContatori();
    } catch (error) {
      console.error("Error saving contatore:", error);
      showToast("Errore nel salvataggio del contatore", "error");
    } finally {
      setSavingContatore(false);
    }
  };

  const handleDeleteContatore = async () => {
    if (!contatoreToDelete) return;
    setSavingContatore(true);
    try {
      await apiRequest(`/contatori/${contatoreToDelete.id}`, {
        method: "DELETE",
      });
      showToast("Contatore eliminato", "success");
      setDeleteContatoreModalOpen(false);
      setContatoreToDelete(null);
      loadContatori();
    } catch (error) {
      console.error("Error deleting contatore:", error);
      showToast("Errore nell'eliminazione del contatore", "error");
    } finally {
      setSavingContatore(false);
    }
  };

  const REGIME_INCENTIVO_LABELS = {
    100000000: "Scambio sul posto",
    100000001: "Ritiro dedicato",
    100000002: "Incentivo RID",
    100000003: "Autoconsumo",
    100000004: "Nessuno",
  };

  const impiantiFVColumns = [
    {
      key: "nome",
      label: "Impianto",
      render: (row) => <span className="font-medium">{row.nome}</span>,
    },
    {
      key: "potenza",
      label: "Potenza",
      render: (row) =>
        row.potenzaInstallata != null ? `${row.potenzaInstallata} kWp` : "-",
    },
    {
      key: "regime",
      label: "Regime GSE",
      render: (row) => REGIME_INCENTIVO_LABELS[row.regimeIncentivo] || "-",
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => row.statoLabel || "-",
    },
  ];

  const contatoriColumns = [
    {
      key: "tipo",
      label: "Tipo",
      render: (row) => {
        const tipo = TIPO_UTENZA_OPTIONS[row.tipoutenza];
        return (
          <span>
            {tipo?.icon || "\uD83D\uDCE6"} {tipo?.label || "N/D"}
          </span>
        );
      },
    },
    {
      key: "codice",
      label: "Codice POD/PDR",
      render: (row) => (
        <span className="font-mono font-medium">{row.codice}</span>
      ),
    },
    {
      key: "fornitore",
      label: "Fornitore",
      render: (row) => row.fornitoreNome || "-",
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) =>
        row.attivo ? (
          <Badge variant="success">Attivo</Badge>
        ) : (
          <Badge variant="default">Inattivo</Badge>
        ),
    },
    ...(canEdit
      ? [
          {
            key: "azioni",
            label: "",
            render: (row) => (
              <div className="flex gap-1 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditContatore(row);
                  }}
                >
                  Modifica
                </Button>
                {canDelete && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setContatoreToDelete(row);
                      setDeleteContatoreModalOpen(true);
                    }}
                  >
                    Elimina
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!immobile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Immobile non trovato</p>
        <Button onClick={() => navigate("/rsa")} className="mt-4">
          Torna alla lista RSA
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <Link to="/rsa" className="hover:text-primary-600">
          RSA
        </Link>
        <span>/</span>
        {immobile.rsaId && (
          <>
            <Link
              to={`/rsa/${immobile.rsaId}`}
              className="hover:text-primary-600"
            >
              {immobile.rsaNome || "RSA"}
            </Link>
            <span>/</span>
          </>
        )}
        <span className="text-gray-900">{immobile.nome}</span>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">
              {immobile.nome}
            </h1>
          </div>
          <p className="text-gray-600">
            {immobile.datiCatastali && immobile.datiCatastali.length > 0 ? (
              <span>
                {immobile.datiCatastali.length}{" "}
                {immobile.datiCatastali.length === 1
                  ? "dato catastale"
                  : "dati catastali"}
                {immobile.renditaCatastaleTotale > 0 && (
                  <span>
                    {" "}
                    | Rendita totale: EUR{" "}
                    {Number(immobile.renditaCatastaleTotale).toLocaleString(
                      "it-IT",
                      { minimumFractionDigits: 2 },
                    )}
                  </span>
                )}
              </span>
            ) : immobile.foglio && immobile.particella ? (
              <span>
                Foglio {immobile.foglio} - Particella {immobile.particella}
                {immobile.subalterno && ` - Sub. ${immobile.subalterno}`}
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/rsa/${immobile.rsaId}`)}
          >
            Torna alla RSA
          </Button>
          {canEdit && (
            <Button onClick={() => navigate(`/immobili/nuovo?id=${id}`)}>
              Modifica
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={() => setDeleteModalOpen(true)}>
              Elimina
            </Button>
          )}
        </div>
      </div>

      {/* Dati Catastali - Full Width */}
      <Card className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span>📋</span> Dati Catastali
          <span className="text-sm font-normal text-gray-500">
            ({immobile.datiCatastali?.length || 0}{" "}
            {(immobile.datiCatastali?.length || 0) === 1 ? "voce" : "voci"})
          </span>
        </h2>

        {immobile.datiCatastali && immobile.datiCatastali.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    Mq
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    Foglio
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    Mappale
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    Sub.
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    Categoria
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    Classe
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">
                    Rendita
                  </th>
                </tr>
              </thead>
              <tbody>
                {immobile.datiCatastali.map((row, idx) => (
                  <tr
                    key={idx}
                    className="border-b last:border-b-0 hover:bg-gray-50"
                  >
                    <td className="px-3 py-2">
                      {row.superficie
                        ? Number(row.superficie).toLocaleString("it-IT")
                        : "-"}
                    </td>
                    <td className="px-3 py-2">{row.foglio || "-"}</td>
                    <td className="px-3 py-2">{row.particella || "-"}</td>
                    <td className="px-3 py-2">{row.subalterno || "-"}</td>
                    <td className="px-3 py-2">{row.categoria || "-"}</td>
                    <td className="px-3 py-2">{row.classe || "-"}</td>
                    <td className="px-3 py-2 text-right">
                      {row.rendita != null && row.rendita !== ""
                        ? `\u20AC ${Number(row.rendita).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
              {immobile.datiCatastali.length > 1 && (
                <tfoot>
                  <tr className="border-t bg-blue-50 font-semibold">
                    <td className="px-3 py-2">
                      {immobile.superficieCatastaleTotale
                        ? `${Number(immobile.superficieCatastaleTotale).toLocaleString("it-IT")} mq`
                        : "-"}
                    </td>
                    <td
                      colSpan={5}
                      className="px-3 py-2 text-right text-gray-600"
                    >
                      Totale rendita:
                    </td>
                    <td className="px-3 py-2 text-right">
                      {immobile.renditaCatastaleTotale
                        ? `\u20AC ${Number(immobile.renditaCatastaleTotale).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
                        : "-"}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            Nessun dato catastale inserito
          </p>
        )}
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dati Tecnici */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🔧</span> Dati Tecnici
          </h2>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-gray-500">Superficie Calpestabile</dt>
              <dd className="font-medium">
                {immobile.superficieCalpestabile
                  ? `${immobile.superficieCalpestabile} mq`
                  : "-"}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Numero Piani</dt>
              <dd className="font-medium">{immobile.numeroPiani || "-"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Numero Stanze</dt>
              <dd className="font-medium">{immobile.numeroStanze || "-"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Posti Letto Autorizzati</dt>
              <dd className="font-medium">
                {immobile.postiLettoAutorizzati || "-"}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Posti Letto Operativi</dt>
              <dd className="font-medium">
                {immobile.postiLettoOperativi || "-"}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Anno Costruzione</dt>
              <dd className="font-medium">{immobile.annoCostruzione || "-"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Ultima Ristrutturazione</dt>
              <dd className="font-medium">
                {immobile.annoUltimaRistrutturazione || "-"}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Tipologia Edificio</dt>
              <dd className="font-medium">
                {TIPOLOGIE_EDIFICIO[immobile.tipologiaEdificio] || "-"}
              </dd>
            </div>
          </dl>
        </Card>

        {/* Indirizzo */}
        {(immobile.indirizzo || immobile.comune) && (
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span>📍</span> Indirizzo
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Indirizzo</dt>
                <dd className="font-medium text-right">
                  {immobile.indirizzo || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Comune</dt>
                <dd className="font-medium">{immobile.comune || "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Provincia</dt>
                <dd className="font-medium">{immobile.provincia || "-"}</dd>
              </div>
            </dl>
          </Card>
        )}

        {/* RSA di appartenenza */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🏥</span> RSA di Appartenenza
          </h2>
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {immobile.rsaNome || "Non specificata"}
            </span>
            {immobile.rsaId && (
              <Link
                to={`/rsa/${immobile.rsaId}`}
                className="text-primary-600 hover:underline text-sm"
              >
                Vai alla RSA
              </Link>
            )}
          </div>
        </Card>
      </div>

      {/* Utenze e Contatori */}
      <Card className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>{"\u26A1"}</span> Utenze e Contatori
              <span className="text-sm font-normal text-gray-500">
                ({contatori.length})
              </span>
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              POD, PDR e punti di fornitura collegati a questo immobile
            </p>
          </div>
          {canEdit && (
            <Button onClick={openCreateContatore}>+ Nuovo Contatore</Button>
          )}
        </div>

        {loadingContatori ? (
          <div className="flex items-center justify-center py-8">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : contatori.length > 0 ? (
          <DataTable
            columns={contatoriColumns}
            data={contatori}
            emptyMessage="Nessun contatore"
            pageSize={5}
          />
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">{"\u26A1"}</div>
            <p className="text-gray-500 mb-4">
              Nessun contatore registrato per questo immobile
            </p>
            {canEdit && (
              <Button onClick={openCreateContatore}>
                Aggiungi primo contatore
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Impianti Fotovoltaici */}
      <Card className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>{"☀️"}</span> Impianti Fotovoltaici
              <span className="text-sm font-normal text-gray-500">
                ({impiantiFV.length})
              </span>
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Impianti FV installati su questo immobile e relativa
              documentazione tecnica
            </p>
          </div>
          {canEdit && (
            <Button
              onClick={() =>
                navigate(
                  `/impianti/nuovo?rsaId=${immobile.rsaId}&immobileId=${id}&tipo=100000000`,
                )
              }
            >
              + Aggiungi impianto
            </Button>
          )}
        </div>

        {loadingImpiantiFV ? (
          <div className="flex items-center justify-center py-8">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : impiantiFV.length > 0 ? (
          <DataTable
            columns={impiantiFVColumns}
            data={impiantiFV}
            emptyMessage="Nessun impianto"
            pageSize={5}
            onRowClick={(row) => navigate(`/impianti/${row.id}`)}
          />
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">{"☀️"}</div>
            <p className="text-gray-500 mb-4">
              Nessun impianto fotovoltaico censito per questo immobile
            </p>
            {canEdit && (
              <Button
                onClick={() =>
                  navigate(
                    `/impianti/nuovo?rsaId=${immobile.rsaId}&immobileId=${id}&tipo=100000000`,
                  )
                }
              >
                Aggiungi primo impianto
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Note */}
      {immobile.note && (
        <Card className="mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📝</span> Note
          </h2>
          <p className="text-gray-700 whitespace-pre-wrap">{immobile.note}</p>
        </Card>
      )}

      {/* Delete Immobile Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Immobile"
        message={`Sei sicuro di voler eliminare l'immobile "${immobile.nome}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
        loading={deleteLoading}
      />

      {/* Contatore Create/Edit Modal */}
      <Modal
        isOpen={contatoreModalOpen}
        onClose={() => setContatoreModalOpen(false)}
        title={editingContatore ? "Modifica Contatore" : "Nuovo Contatore"}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Codice POD/PDR *"
              name="codice"
              value={contatoreForm.codice}
              onChange={handleContatoreFormChange}
              placeholder="es. IT001E00012345"
            />
            <Select
              label="Tipo Utenza"
              name="tipoutenza"
              value={contatoreForm.tipoutenza}
              onChange={handleContatoreFormChange}
              options={[
                { value: "", label: "Seleziona tipo" },
                ...Object.entries(TIPO_UTENZA_OPTIONS).map(([value, opt]) => ({
                  value,
                  label: `${opt.icon} ${opt.label}`,
                })),
              ]}
            />
            <Select
              label="Fornitore"
              name="fornitoreId"
              value={contatoreForm.fornitoreId}
              onChange={handleContatoreFormChange}
              options={[
                { value: "", label: "Seleziona fornitore (opzionale)" },
                ...fornitoriList.map((f) => ({
                  value: f.id,
                  label: f.nome,
                })),
              ]}
            />
            <Input
              label="Codice Cliente"
              name="codicecliente"
              value={contatoreForm.codicecliente}
              onChange={handleContatoreFormChange}
              placeholder="Codice cliente presso fornitore"
            />
            <Input
              label="Indirizzo fornitura"
              name="indirizzo"
              value={contatoreForm.indirizzo}
              onChange={handleContatoreFormChange}
              placeholder="Se diverso dall'immobile"
            />
            <Input
              label="Potenza impegnata (kW)"
              name="potenzaimpegnata"
              type="number"
              step="0.01"
              value={contatoreForm.potenzaimpegnata}
              onChange={handleContatoreFormChange}
              placeholder="Solo per energia elettrica"
            />
            <Input
              label="Data attivazione"
              name="dataattivazione"
              type="date"
              value={contatoreForm.dataattivazione}
              onChange={handleContatoreFormChange}
            />
            <Input
              label="Data disattivazione"
              name="datadisattivazione"
              type="date"
              value={contatoreForm.datadisattivazione}
              onChange={handleContatoreFormChange}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="contatore-attivo"
              name="attivo"
              checked={contatoreForm.attivo}
              onChange={handleContatoreFormChange}
              className="rounded border-gray-300"
            />
            <label
              htmlFor="contatore-attivo"
              className="text-sm font-medium text-gray-700"
            >
              Contratto attivo
            </label>
          </div>
          <Textarea
            label="Note"
            name="note"
            value={contatoreForm.note}
            onChange={handleContatoreFormChange}
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setContatoreModalOpen(false)}
            >
              Annulla
            </Button>
            <Button onClick={handleSaveContatore} disabled={savingContatore}>
              {savingContatore
                ? "Salvataggio..."
                : editingContatore
                  ? "Salva Modifiche"
                  : "Crea Contatore"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Contatore Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteContatoreModalOpen}
        onClose={() => {
          setDeleteContatoreModalOpen(false);
          setContatoreToDelete(null);
        }}
        onConfirm={handleDeleteContatore}
        title="Elimina Contatore"
        message={`Sei sicuro di voler eliminare il contatore "${contatoreToDelete?.codice}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
        loading={savingContatore}
      />
    </div>
  );
}

export default DettaglioImmobilePage;
